// useChat.js
import { ref } from 'vue';
import { MetaData, TdChatItemProps } from './type';
import { formatDateByRule, getUrl, getMofaUrl } from './util';
import { sseRequest, fetchSSE } from './sseRequest';
import { sseRequest as sseRequestHunyuan, fetchSSE as fetchSSEHunyuan } from './sseRequest-hunyuan';

// @ts-ignore
export function useChat(props, emit) {
  const welcome = props.welcome || [];
  const apiKey = props.apiKey || '';
  const assistantMeta: MetaData = props?.assistantMeta || {};
  const userMeta: MetaData = props?.userMeta || {};
  const chatList = ref<TdChatItemProps[]>([]);
  const loading = ref(false);
  const fetchCancel = ref(null);
  const isStreamLoad = ref(false);
  const controller = new AbortController();
  const { signal } = controller;

  // 获取真实用户和AI信息
  if (welcome.length > 0) {
    welcome.forEach((item: string) => {
      chatList.value.unshift({
        avatar: assistantMeta?.avatar,
        name: assistantMeta?.name,
        datetime: formatDateByRule(new Date(), 'yyyy/MM/dd hh:mm:ss'),
        content: item,
        role: 'system',
      });
    });
  }
  // 监听abort事件
  signal.addEventListener('abort', () => {
    try {
      onAbort && onAbort();
    } catch {}
  });

  const clearConfirm = function (context: { e: MouseEvent }) {
    chatList.value = [];
    emit('clear', context);
  };

  const onStop = function () {
    if (controller && isStreamLoad.value) {
      // controller.abort(); //自己内部abort不生效
      fetchCancel.value.abort();
      // addEventListener('abort'）不生效，提前改变状态变量
      loading.value = false;
      isStreamLoad.value = false;
    }
  };

  // 区分不同组件的调用方法去请求接口
  const innerFetchSSE = async function (url: string, params: Object, sseRequest: Function) {
    const lastItem = chatList.value[0];
    sseRequest(
      {
        url,
        data: params,
        success(result: any) {
          loading.value = false;
          const { type, data } = result;
          if (type === 'error') {
            Object.assign(lastItem, {
              role: 'error',
              content: data.response,
            });
          } else if (type === 'delta') {
            lastItem.content += data?.delta.content;
            // 显示更多
          } else {
            // 兼容只有一条数据展示兜底文案
            if (data?.response) {
              lastItem.content = data?.response;
            }
          }
        },
        cancel(cancel: Function) {
          fetchCancel.value = cancel;
        },
        abort() {
          // 停止请求
          isStreamLoad.value = false;
          loading.value = false;
        },
        complete(isOk: Boolean, msg: string) {
          // 兼容500错误 fail和complete逻辑混在一起待优化
          if (!isOk || !lastItem.content) {
            lastItem.role = 'error';
            lastItem.content = msg;
          }
          isStreamLoad.value = false;
          loading.value = false;
        },
      },
      apiKey,
    );
  };
  const operation = (type: string, options: { e: MouseEvent; index: number }) => {
    const index = options.index;
    const item = chatList.value[index];
    if (type === 'replay') {
      // 重新生成
      const userQuery = chatList.value[index + 1].content;
      sendRequest('hunyuan', userQuery as string, props.model || props.mofaId);
    }
    emit('operation', type, {
      ...options,
      item,
    });
  };
  // model占位表示 hunyuan 模型参数或者mofa的id
  const getParams = (type: string, inputValue: string, modelOrMofaId?: string) => {
    const paramsMap = {
      hunyuan: {
        reqData: {
          messages: [
            {
              role: 'user',
              content: inputValue,
            },
          ],
          model: modelOrMofaId || props.model,
          stream: true,
        },
        url: getUrl(props.model),
        sseRequest: sseRequestHunyuan,
        fetchSSE: fetchSSEHunyuan, // 提供给chatRequest使用
      },
      mofa: {
        reqData: {
          query: inputValue,
          history: [] as any[],
          mofa_id: modelOrMofaId || props.mofaId,
          contextFlag: 1,
        },
        url: getMofaUrl(),
        sseRequest,
        fetchSSE, // 提供给chatRequest使用
      },
    };
    return paramsMap[type];
  };
  const setChatList = (inputValue: string) => {
    // 支持函数传入和属性获取
    if (loading.value) {
      return;
    }
    if (!inputValue) return;
    if (props.showUserMsg) {
      chatList.value.unshift({
        avatar: userMeta?.avatar,
        name: userMeta?.name,
        datetime: new Date().toDateString(),
        content: inputValue,
        role: 'user',
      });
    }
    // 空消息占位
    chatList.value.unshift({
      avatar: assistantMeta?.avatar,
      name: assistantMeta?.name,
      datetime: new Date().toDateString(),
      content: '',
      role: 'assistant',
    });
  };

  // 监听到abort不生效提到cancel里处理-待优化废弃
  const onAbort = () => {
    // 停止请求
    isStreamLoad.value = false;
    loading.value = false;
  };
  const sendRequest = async function (type: string, inputValue: string, modelOrMofaId?: string) {
    // 支持函数传入和属性获取
    setChatList(inputValue);
    loading.value = true;
    // 标记流式接口数据加载完成
    isStreamLoad.value = true;
    const lastItem = chatList.value[0];
    const { reqData, url, sseRequest, fetchSSE } = getParams(type, inputValue, modelOrMofaId);
    // 外部自定义请求
    if (props.chatRequest) {
      await fetchSSE(
        () =>
          props.chatRequest({
            messages: [
              {
                role: 'user',
                content: inputValue,
              },
            ],
            cancel(cancel: Function) {
              fetchCancel.value = cancel;
            },
            // signal, 想内置abort，未生效待排查 todo
          }),
        {
          success(result: any) {
            loading.value = false;
            const { type, data } = result;
            if (type === 'error') {
              Object.assign(lastItem, {
                role: 'error',
                content: data.response,
              });
            } else if (type === 'delta') {
              lastItem.content += data?.delta.content;
              // 显示更多
            } else {
              // 兼容只有一条数据展示兜底文案
              if (data?.response) {
                lastItem.content = data?.response;
              }
            }
          },
          complete(isOk: Boolean, msg: string) {
            // 兼容500错误 fail和complete逻辑混在一起待优化
            if (!isOk || !lastItem.content) {
              lastItem.role = 'error';
              lastItem.content = msg;
            }
            isStreamLoad.value = false;
            loading.value = false;
          },
        },
      );
      return;
    }
    if (!url) return;
    // 内置流式接口请求
    innerFetchSSE(url, reqData, sseRequest);
  };
  return {
    chatList,
    loading,
    isStreamLoad,
    onStop,
    setChatList,
    operation,
    clearConfirm,
    sendRequest,
  };
}
